import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'

interface TourStep {
  selector: string
  title: string
  body: string
  placement: 'top' | 'bottom' | 'left' | 'right'
}

const STEPS: TourStep[] = [
  {
    selector: '[data-sheet-tour="kind"]',
    title: 'Tipo do lançamento',
    body: 'Escolha Entrada para receitas (salário, freelance…) ou Saída para despesas (mercado, conta, lazer…).',
    placement: 'bottom',
  },
  {
    selector: '[data-sheet-tour="title"]',
    title: 'Descrição',
    body: 'Dê um nome claro para identificar este lançamento. Ex: Mercado mensal, Salário, Netflix.',
    placement: 'bottom',
  },
  {
    selector: '[data-sheet-tour="amount"]',
    title: 'Valor',
    body: 'Digite o valor em reais. Só os números — a vírgula e os centavos entram automaticamente.',
    placement: 'bottom',
  },
  {
    selector: '[data-sheet-tour="category"]',
    title: 'Categoria',
    body: 'Classifique o lançamento. Isso alimenta os gráficos de gastos e relatórios mensais.',
    placement: 'top',
  },
  {
    selector: '[data-sheet-tour="recurring"]',
    title: 'Lançamento recorrente',
    body: 'Marque aqui se essa despesa ou receita se repete todo mês. O BagCash lançará automaticamente.',
    placement: 'top',
  },
]

const PAD = 8
const GAP = 14
const TOOLTIP_W = 280
const MARGIN = 12

interface SpotRect { top: number; left: number; width: number; height: number }

interface Props {
  active: boolean
  onDone: () => void
}

export default function AddTransactionTour({ active, onDone }: Props) {
  const [stepIndex,   setStepIndex]   = useState(0)
  const [spotRect,    setSpotRect]    = useState<SpotRect | null>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const nextBtnRef = useRef<HTMLButtonElement>(null)

  const measure = useCallback(() => {
    if (!active) return
    const step = STEPS[stepIndex]
    const el = document.querySelector(step.selector)
    if (!el) { setSpotRect(null); return }
    el.scrollIntoView({ behavior: 'instant', block: 'nearest' })
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect()
      setSpotRect({
        top:    r.top    - PAD,
        left:   r.left   - PAD,
        width:  r.width  + PAD * 2,
        height: r.height + PAD * 2,
      })
    })
  }, [stepIndex, active])

  useLayoutEffect(() => { measure() }, [measure])

  useEffect(() => {
    window.addEventListener('resize', measure, { passive: true })
    window.addEventListener('scroll', measure, { passive: true, capture: true })
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, { capture: true })
    }
  }, [measure])

  useEffect(() => {
    if (active) setStepIndex(0)
  }, [active])

  useEffect(() => {
    if (!isAnimating && active) {
      const t = setTimeout(() => nextBtnRef.current?.focus(), 80)
      return () => clearTimeout(t)
    }
  }, [stepIndex, isAnimating, active])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onDone() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onDone])

  if (!active) return null

  function advance() {
    if (stepIndex >= STEPS.length - 1) { onDone(); return }
    setIsAnimating(true)
    setTimeout(() => {
      setStepIndex(i => i + 1)
      setIsAnimating(false)
    }, 180)
  }

  const step = STEPS[stepIndex]
  const { placement } = step

  let tooltipStyle: React.CSSProperties = {}
  if (spotRect) {
    const APPROX_H = 160
    if (placement === 'bottom') {
      let left = spotRect.left + spotRect.width / 2 - TOOLTIP_W / 2
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - MARGIN))
      const top = Math.min(spotRect.top + spotRect.height + GAP, window.innerHeight - APPROX_H - MARGIN)
      tooltipStyle = { top, left }
    } else if (placement === 'top') {
      let left = spotRect.left + spotRect.width / 2 - TOOLTIP_W / 2
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - MARGIN))
      const bottom = window.innerHeight - spotRect.top + GAP
      tooltipStyle = { bottom: Math.max(MARGIN, bottom), left }
    } else if (placement === 'right') {
      const left = Math.min(spotRect.left + spotRect.width + GAP, window.innerWidth - TOOLTIP_W - MARGIN)
      let top = spotRect.top + spotRect.height / 2 - APPROX_H / 2
      top = Math.max(MARGIN, Math.min(top, window.innerHeight - APPROX_H - MARGIN))
      tooltipStyle = { top, left }
    } else if (placement === 'left') {
      const right = window.innerWidth - spotRect.left + GAP
      let top = spotRect.top + spotRect.height / 2 - APPROX_H / 2
      top = Math.max(MARGIN, Math.min(top, window.innerHeight - APPROX_H - MARGIN))
      tooltipStyle = { top, right: Math.max(MARGIN, right) }
    }
  }

  const isLast = stepIndex === STEPS.length - 1

  return (
    <>
      <div className="bc-tour__backdrop" onClick={onDone} aria-hidden="true" />

      {spotRect && (
        <div
          className={'bc-tour__spotlight' + (isAnimating ? ' is-hidden' : '')}
          style={{
            top:    spotRect.top,
            left:   spotRect.left,
            width:  spotRect.width,
            height: spotRect.height,
          }}
          aria-hidden="true"
        />
      )}

      {spotRect && (
        <div
          className="bc-tour__tooltip"
          data-placement={placement}
          style={tooltipStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Dicas do formulário"
        >
          <div className="bc-tour__step-count">Passo {stepIndex + 1} de {STEPS.length}</div>
          <h2 className="bc-tour__title">{step.title}</h2>
          <p className="bc-tour__body">{step.body}</p>
          <div className="bc-tour__footer">
            <button className="bc-tour__btn-skip" onClick={onDone}>Pular</button>
            <div className="bc-tour__dots" aria-hidden="true">
              {STEPS.map((_, i) => (
                <div key={i} className={'bc-tour__dot' + (i === stepIndex ? ' is-active' : '')} />
              ))}
            </div>
            <button
              ref={nextBtnRef}
              className="bc-btn bc-btn--primary bc-btn--sm"
              onClick={advance}
            >
              {isLast ? 'Concluir' : 'Próximo'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
